import { generateUUID } from './strings';

/**
 * Query parsing and manipulation utilities
 */

export interface QuerySegment {
  query: string;
  rowStart: number;
  columnStart: number;
  rowEnd: number;
  columnEnd: number;
}

export interface QueryPosition {
  row?: number;
  column?: number;
}

export interface LineColumnResult {
  startRow?: number;
  startCol?: number;
  endRow?: number;
  endCol?: number;
}

/**
 * Removes SQL comments from the beginning of a query
 * Uses iterative approach to avoid ReDoS vulnerability from nested quantifiers
 */
export const removeSqlCommentsStart = (query: string): string => {
  let result = (query || '').trimStart();

  // Iteratively remove comments from the start until no more found
  let changed = true;
  while (changed && result.length > 0) {
    changed = false;

    // Check for block comment /* ... */
    if (result.startsWith('/*')) {
      const endIndex = result.indexOf('*/', 2);
      if (endIndex !== -1) {
        result = result.slice(endIndex + 2).trimStart();
        changed = true;
        continue;
      }
    }

    // Check for line comment -- ...
    if (result.startsWith('--')) {
      const newlineIndex = result.search(/\r?\n/);
      if (newlineIndex !== -1) {
        result = result.slice(newlineIndex + 1).trimStart();
      } else {
        result = ''; // Comment extends to end of string
      }
      changed = true;
    }
  }

  return result;
};

/**
 * Removes all comments from SQL text
 * Uses character-by-character parsing to avoid ReDoS vulnerability
 */
export const removeCommentsFromSql = (sqlText: string): string => {
  const result: string[] = [];
  let i = 0;
  const len = sqlText.length;

  while (i < len) {
    const char = sqlText[i];
    const nextChar = i + 1 < len ? sqlText[i + 1] : '';

    // Check for string literals - skip over them entirely
    if (char === "'" || char === '"' || char === '`') {
      const quote = char;
      result.push(char);
      i++;
      // Find the end of the string literal
      while (i < len) {
        const c = sqlText[i]!;
        result.push(c);
        if (c === quote) {
          // Check for escaped quote (doubled)
          if (i + 1 < len && sqlText[i + 1] === quote) {
            result.push(sqlText[i + 1]!);
            i += 2;
          } else {
            i++;
            break;
          }
        } else {
          i++;
        }
      }
      continue;
    }

    // Check for block comment /* ... */
    if (char === '/' && nextChar === '*') {
      let newlineCount = 0;
      i += 2; // Skip /*
      // Find the end of the block comment
      while (i < len) {
        if (sqlText[i] === '\n') {
          newlineCount++;
        }
        if (sqlText[i] === '*' && i + 1 < len && sqlText[i + 1] === '/') {
          i += 2; // Skip */
          break;
        }
        i++;
      }
      // Preserve line count
      result.push('\n'.repeat(newlineCount));
      continue;
    }

    // Check for line comment -- ...
    if (char === '-' && nextChar === '-') {
      i += 2; // Skip --
      // Skip until end of line or end of string
      while (i < len && sqlText[i] !== '\n' && sqlText[i] !== '\r') {
        i++;
      }
      continue;
    }

    // Regular character
    result.push(char!);
    i++;
  }

  return result.join('');
};

/**
 * Splits SQL text by semicolons, respecting quotes and comments
 */
export const splitTextBySemicolonNew = (
  text: string,
  isFormat: boolean = false,
  start: QueryPosition = {}
): QuerySegment[] => {
  const segments: string[] = [];
  const queries: QuerySegment[] = [];
  let currentSegment = '';
  let rowStart = start?.row || 0;
  let columnStart = start?.column || 0;
  let currentRow = start?.row || 0;
  let currentCol = start?.column || 0;
  let insideSingleQuotes = false;
  let insideDoubleQuotes = false;
  let insideTemplateLiteral = false;
  let insideComment = false;
  let singleLineComment = false;
  let needsStartUpdate = false; // Track if we need to update start position

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const prevChar = text?.[i - 1];
    const nextChar = text?.[i + 1];

    if (char === '\n') {
      currentRow += 1;
    }
    if (prevChar === '\n') {
      currentCol = 0;
      singleLineComment = false;
    } else if (i > 0) {
      currentCol += 1;
    }

    if (char === "'" && !singleLineComment && !insideComment) {
      insideSingleQuotes = !insideSingleQuotes;
    } else if (char === '"' && !singleLineComment && !insideComment) {
      insideDoubleQuotes = !insideDoubleQuotes;
    } else if (char === '`' && !singleLineComment && !insideComment) {
      insideTemplateLiteral = !insideTemplateLiteral;
    } else if ((char === '/' && nextChar === '*') || (char === '*' && nextChar === '/')) {
      insideComment = !insideComment;
    } else if (char === '-' && nextChar === '-') {
      if (insideSingleQuotes || insideDoubleQuotes) {
        singleLineComment = false;
      } else {
        singleLineComment = true;
      }
    }

    if (
      char === ';' &&
      !insideSingleQuotes &&
      !insideDoubleQuotes &&
      !insideTemplateLiteral &&
      !insideComment &&
      !singleLineComment
    ) {
      segments.push(isFormat ? `${currentSegment};` : currentSegment);
      queries.push({
        query: isFormat ? `${currentSegment};` : currentSegment,
        rowStart,
        columnStart,
        rowEnd: currentRow,
        columnEnd: currentCol,
      });
      // Mark that we need to find where the next query actually starts
      needsStartUpdate = true;
      currentSegment = '';
    } else {
      // Update start position when we encounter the first non-whitespace char of a new segment
      if (needsStartUpdate && char?.trim() !== '') {
        rowStart = currentRow;
        columnStart = currentCol;
        needsStartUpdate = false;
      }
      currentSegment += char;
    }
  }

  // For the last segment, update start if needed
  if (needsStartUpdate && currentSegment.trim() !== '') {
    // Find the first non-whitespace position in the last segment
    const trimmedStart = currentSegment.search(/\S/);
    if (trimmedStart >= 0) {
      // Calculate actual position by finding where non-whitespace starts
      let tempRow = rowStart;
      let tempCol = columnStart;
      for (let j = 0; j < trimmedStart; j++) {
        if (currentSegment[j] === '\n') {
          tempRow++;
          tempCol = 0;
        } else {
          tempCol++;
        }
      }
      rowStart = tempRow;
      columnStart = tempCol;
    }
  }

  // Push the last segment after the loop
  queries.push({
    query: currentSegment,
    rowStart,
    columnStart,
    rowEnd: currentRow,
    columnEnd: currentCol,
  });
  segments.push(currentSegment);

  return queries;
};

/**
 * Checks whether a SQL query is a SELECT statement.
 */
export const isSelectQuery = (query: string): boolean => {
  return /^\s*select\b/i.test(query.trim());
};

/**
 * Checks whether a SQL query already has a trailing LIMIT clause.
 * Strips trailing semicolons, whitespace, and single-line comments before checking,
 * so that:
 *   - "SELECT * FROM t LIMIT 50" → true
 *   - "SELECT * FROM t LIMIT 50 OFFSET 10" → true
 *   - "SELECT credit_limit FROM t" → false (column name, not clause)
 *   - "SELECT * FROM (SELECT id FROM t LIMIT 10)" → false (subquery limit only)
 *   - "SELECT * FROM t; -- LIMIT 100" → false (commented out)
 */
export const hasTrailingLimit = (query: string): boolean => {
  let cleaned = query.trim();

  // Strip trailing semicolons (iterative — no regex backtracking)
  let end = cleaned.length;
  while (end > 0 && (cleaned[end - 1] === ';' || cleaned[end - 1] === ' ')) {
    end--;
  }
  cleaned = cleaned.slice(0, end);

  // Strip trailing single-line comment: find the last "--" that isn't inside quotes
  const lastComment = cleaned.lastIndexOf('--');
  if (lastComment !== -1 && !cleaned.slice(lastComment).includes('\n')) {
    cleaned = cleaned.slice(0, lastComment).trim();
  }

  // Check if query ends with LIMIT <number> (with optional OFFSET <number>)
  // Using a $ anchor with simple non-overlapping groups — no catastrophic backtracking
  return /\blimit\s+\d+(?:\s+offset\s+\d+)?\s*$/i.test(cleaned);
};

/**
 * Applies a LIMIT clause to an array of SQL queries.
 * Only appends LIMIT to SELECT statements that don't already have a trailing LIMIT.
 * Non-SELECT statements (INSERT, CREATE, DROP, etc.) are returned unchanged.
 */
export const applyLimitToQueries = (queries: string[], limitValue: number | string): string[] => {
  if (!limitValue) return queries;

  return queries.map((query) => {
    const trimmed = query.trim();
    if (!isSelectQuery(trimmed)) return query;
    if (hasTrailingLimit(trimmed)) return query;
    return query + ' LIMIT ' + limitValue;
  });
};

interface QueryData {
  query?: string;
  rowStart?: number;
  columnStart?: number;
}

/**
 * Extracts line and column information from error messages
 */
export const extractLineAndColumnV2 = (
  error: string,
  data?: QueryData,
  isRow: boolean = false
): string | LineColumnResult => {
  const { query = '', rowStart = 0, columnStart } = data || {};

  const blankLinesCount = (query.match(/^\s*\n+/)?.[0] || '')?.split('\n').length - 1;

  // Split the string by line breaks
  const lines = (data?.query || '').split('\n');

  // Get the last line
  const lastLine = lines?.[blankLinesCount];

  let startRow: number | undefined;
  let startCol: number | undefined;
  let endRow: number | undefined;
  let endCol: number | undefined;

  let updatedError = (error || '').replace(
    /(From line | At line )(\d+)(, column )(\d+)/i,
    (_match, linePrefix, lineNumber, columnPrefix, columnNumber) => {
      startRow = parseInt(lineNumber, 10) + rowStart;
      const spaceCount = blankLinesCount === 0 ? lastLine?.match(/^ */)?.[0]?.length || 0 : 0;
      startCol =
        parseInt(columnNumber, 10) +
          (startRow === rowStart + 1 ? columnStart || 0 : 0) +
          spaceCount || 0;
      return `${linePrefix}${startRow}${columnPrefix}${startCol}`;
    }
  );

  updatedError = (updatedError || '').replace(
    /(to line )(\d+)(, column )(\d+)/i,
    (_match, linePrefix, lineNumber, columnPrefix, columnNumber) => {
      endRow = parseInt(lineNumber, 10) + rowStart;
      const spaceCount = blankLinesCount === 0 ? lastLine?.match(/^ */)?.[0]?.length || 0 : 0;
      endCol =
        parseInt(columnNumber, 10) +
          (endRow === (endRow || 0) + 1 ? columnStart || 0 : 0) +
          spaceCount || 0;
      return `${linePrefix}${endRow}${columnPrefix}${endCol}`;
    }
  );

  if (isRow) {
    console.log(startRow, endRow, startCol, endCol);
    return { startRow, startCol, endRow, endCol };
  }

  return updatedError;
};

interface QueryObjResult {
  runningQueries: string[];
  queryList: Record<
    string,
    {
      query: string;
      queryError: string;
      queryID: string;
      isCached: string;
      isRunning: boolean;
      queryStatus: string;
    }
  >;
}

/**
 * Creates a query object with running queries and query list
 */
export const createQueryObj = (data: Array<{ query: string }>): QueryObjResult => {
  const runningQueries: string[] = [];
  const queryList: Record<string, unknown> = {};

  (data || [])?.forEach((item, index) => {
    const id = generateUUID();
    runningQueries.push(id);
    queryList[id] = {
      ...item,
      queryError: '',
      queryID: '',
      isCached: '',
      isRunning: false,
      queryStatus: index === 0 ? 'Connecting with engine...' : '',
    };
  });

  return { runningQueries, queryList } as QueryObjResult;
};

/**
 * Counts empty items at start and end of an array
 */
interface SearchRange {
  start: { row: number; column?: number };
  end?: { row: number; column?: number };
}

interface CountResult {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

export const countEmptyItems = (arr: string[], searchRange: SearchRange): CountResult => {
  let startCount = 0;
  let endCount = 0;
  const startRow = searchRange.start.row;
  const endRow = searchRange?.end?.row || 0;

  // Count empty items at the start
  for (const item of arr) {
    if (!item?.trim()) {
      startCount++;
    } else {
      break;
    }
  }

  // Count empty items at the end
  for (let i = arr.length - 1; i >= 0; i--) {
    if (!arr[i]?.trim()) {
      endCount++;
    } else {
      break;
    }
  }

  const startLine = arr?.[startCount];
  const leadingSpaces = startLine?.match(/^\s*/);
  let startColumn = leadingSpaces ? (leadingSpaces[0]?.length ?? 0) : 0;
  const endLine = arr?.[arr.length - 1 - endCount];
  let endColumn = (endLine || '').trimEnd()?.length;
  startColumn = !startCount ? searchRange?.start?.column || 0 : startColumn;
  endColumn = !endCount ? searchRange?.end?.column || 0 : endColumn;

  return {
    startRow: startRow + startCount,
    endRow: endRow - endCount,
    startColumn,
    endColumn: endColumn + 1,
  };
};
